import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API_BASE, testIds } from "@/lib/api";

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/contact`, form);
      toast.success("Message sent — I'll be in touch within 48 hours.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={testIds.contact.section}
      className="py-24 md:py-32 px-6 md:px-12"
    >
      <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-5">
          <div className="overline mb-4">Get in Touch</div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.02] text-[#2D372B]">
            Not sure where
            <br />
            <span className="italic text-[#AD6A48]">to start?</span>
          </h2>
          <p className="mt-6 text-[#5C665A] leading-relaxed">
            Tell me a little about where you are and what you're hoping for.
            I'll write back within 48 hours with an honest recommendation —
            even if it's not one of my programs.
          </p>

          <div className="mt-10 space-y-3 text-sm text-[#5C665A]">
            <div>
              <span className="text-[#2D372B] font-medium">sanbhan1812@gmail.com</span>
            </div>
            <div>Mon – Fri · 8am – 6pm PT</div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="md:col-span-7 bg-white border border-[#E5E2D9] rounded-3xl p-8 md:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-name" className="text-[#2D372B]">Your name</Label>
              <Input
                id="c-name"
                data-testid={testIds.contact.inputName}
                value={form.name}
                onChange={set("name")}
                placeholder="Jane Doe"
                className="bg-[#F7F5F0] border-[#E5E2D9]"
                required
              />
            </div>
            <div>
              <Label htmlFor="c-email" className="text-[#2D372B]">Email</Label>
              <Input
                id="c-email"
                type="email"
                data-testid={testIds.contact.inputEmail}
                value={form.email}
                onChange={set("email")}
                placeholder="jane@example.com"
                className="bg-[#F7F5F0] border-[#E5E2D9]"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="c-subject" className="text-[#2D372B]">Subject (optional)</Label>
            <Input
              id="c-subject"
              data-testid={testIds.contact.inputSubject}
              value={form.subject}
              onChange={set("subject")}
              placeholder="Bootcamp inquiry"
              className="bg-[#F7F5F0] border-[#E5E2D9]"
            />
          </div>

          <div>
            <Label htmlFor="c-message" className="text-[#2D372B]">Your message</Label>
            <Textarea
              id="c-message"
              data-testid={testIds.contact.inputMessage}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell me about your goals…"
              className="bg-[#F7F5F0] border-[#E5E2D9] min-h-[140px]"
              required
            />
          </div>

          <button
            type="submit"
            data-testid={testIds.contact.submit}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#89937C] text-white font-medium hover:bg-[#737D67] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Send message
          </button>
        </form>
      </div>
    </section>
  );
}
